/**
 * @file  open-sdk的公共接口 （Public API）
 * @description 此文件中的 export 将对外部项目可见，请勿添加仅用于 SDK 内部的interface/type。
 * @public
 */
import { LOCALE_KEY } from '@fx-ui/intl';

/**
 * 插件通用参数、请求参数可配置字段类型
 */
export enum PLUGIN_ARGUMENT_FIELD_TYPE {
    TEXT = 'text',
    NUMBER = 'number',
    DATETIME = 'datetime',
    SELECT = 'select',
    USER = 'user',
    DEPT = 'dept',
    VECTOR = 'vector'
}

// 插件入参赋值规则
export enum PLUGIN_ARGUMENT_VALUE_FROM {
    /** 自定义 */
    LITERAL = 'literal',
    /** 表单字段值 */
    PROCESS_VAR = 'process_var',
    /** 组合（literal + process_var），只有 TEXT 类型参数支持组合 */
    EXPR = 'expr',
    /** 子表单 */
    VECTOR = 'vector'
}

/**
 * 插件通用参数、请求参数字段数据结构
 */
export type PluginArgumentFieldId = string;

export interface IPluginArgumentField {
    id: PluginArgumentFieldId; // ID
    label: string; // 显示名称
    description: string; // 提示内容
    fieldType: PLUGIN_ARGUMENT_FIELD_TYPE; // 字段类型
    isHidden?: boolean; // 是否可见
    isRequired: boolean; // 是否必填
    isEnabled: boolean; // 是否允许修改
    fieldConf: {};
    defaultValue?: unknown; // 默认值
}

// 文本
export interface TextArgumentField extends IPluginArgumentField {
    fieldType: PLUGIN_ARGUMENT_FIELD_TYPE.TEXT;
    fieldConf: Partial<{
        placeholder: string;
        regexp: string; // 正则对象缓存
        isMultiLine: boolean; // 单行/多行
    }>;
    defaultValue?: string;
}

// 数字
export interface NumberArgumentField extends IPluginArgumentField {
    fieldType: PLUGIN_ARGUMENT_FIELD_TYPE.NUMBER;
    fieldConf: Partial<{
        maxValue: number;
        minValue: number;
    }>;
    defaultValue?: number;
}

// 日期时间
export interface DateArgumentField extends IPluginArgumentField {
    fieldType: PLUGIN_ARGUMENT_FIELD_TYPE.DATETIME;
    fieldConf: Partial<{
        placeholder: string;
        time: string;
        format: string;
    }>;
    defaultValue?: string;
}

// 下拉框
export interface SelectArgumentField extends IPluginArgumentField {
    fieldType: PLUGIN_ARGUMENT_FIELD_TYPE.SELECT;
    fieldConf: {
        items: { value: string; text: string }[]; // 下拉选项
    };
    defaultValue?: string;
}

// 成员选择器
export interface UserArgumentField extends IPluginArgumentField {
    fieldType: PLUGIN_ARGUMENT_FIELD_TYPE.USER;
}

// 部门选择器
export interface DeptArgumentField extends IPluginArgumentField {
    fieldType: PLUGIN_ARGUMENT_FIELD_TYPE.DEPT;
}

// vector
export interface VectorArgumentField extends IPluginArgumentField {
    fieldType: PLUGIN_ARGUMENT_FIELD_TYPE.VECTOR;
    fieldConf: {
        fields: PluginArgumentField[];
        // 参数赋值规则，默认为 LITERAL
        resolvers?: Record<
            string,
            | PLUGIN_ARGUMENT_VALUE_FROM.LITERAL
            | PLUGIN_ARGUMENT_VALUE_FROM.PROCESS_VAR
            | PLUGIN_ARGUMENT_VALUE_FROM.EXPR
        >;
        sizeLimit?: number;
    };
    defaultValue?: BaseArgumentValue[][];
}

export type PluginArgumentField =
    | TextArgumentField
    | NumberArgumentField
    | DateArgumentField
    | SelectArgumentField
    | VectorArgumentField
    | UserArgumentField
    | DeptArgumentField;

/**
 * 插件收费方式
 */
export enum PLUGIN_PAYMENT_TYPE {
    FREE = 'free', // 免费
    SUBSCRIPTION = 'subscription' // 订阅制
}

/** 函数付费方式 */
export enum PLUGIN_FN_PAYMENT_TYPE {
    FREE = 'free',
    PAY_AS_YOU_GO = 'pay_as_you_go' // 按次付费
}

/**
 * 免费插件付费策略
 */
export interface IPluginFreePayment {
    type: PLUGIN_PAYMENT_TYPE.FREE;
}

/** 函数付费策略-免费 */
export interface IPluginFnFreePayment {
    type: PLUGIN_FN_PAYMENT_TYPE.FREE;
}

/**
 * 按次付费插件付费策略
 */
export interface IPluginPayAsYouGoPayment {
    type: PLUGIN_FN_PAYMENT_TYPE.PAY_AS_YOU_GO;
    unit: number; // 度量单位.
    count: number; // 单位扣费(简道币).
    price?: number; // 单位扣费(云币).
}

/**
 * 插件订阅类型
 * - paid: 购买
 * - trial: 试用
 */
export type PluginSubscriptionType = 'paid' | 'trial';

/**
 * 订阅式插件付费策略
 */
export interface IPluginSubscriptionPayment {
    type: PLUGIN_PAYMENT_TYPE.SUBSCRIPTION;
    /** 套餐 */
    plans: {
        /** 套餐时长 */
        days: number;
        /** 套餐价格 */
        price?: number;
        /** 后端自动生成uuid */
        id?: string;
        /** 套餐类型 */
        type?: PluginSubscriptionType;
    }[];
}

export type PluginPayment = IPluginFreePayment | IPluginSubscriptionPayment;

export type PluginFunctionPayment = IPluginFnFreePayment | IPluginPayAsYouGoPayment;

export interface IPluginTriggerFee {
    symbol: string;
    payment: PluginFunctionPayment;
}

/**
 * 插件生命周期
 */
export enum PLUGIN_STAGE {
    PRE_ALPHA = 'pre_alpha', // 展示但不可安装, "即将上线".
    ALPHA = 'alpha', // 内测, 仅带有特殊标识的用户可安装.
    BETA = 'beta', // 公测, 仅开放测试期间可安装.
    GA = 'ga', // Stable Release, 全部用户可安装, General Availability.
    EOL = 'eol' // 生命周期结束, 插件下架, 不可安装.
}

/**
 * 插件发布状态
 */
export enum PLUGIN_PUBLISH_STATUS {
    IS_RELEASED, // 上架
    NOT_RELEASED, // 下架
    BEFORE_RELEASE // 待上架
}

// 插件详情截图、视频等资源
export interface IPluginScreenshot {
    _id: string;
    mime: string; // - MIME 类型
    url: string; // - 资源地址
    name?: string; // - 资源名称
    size?: number; // - 资源大小
}

/**
 * 自建插件 - 鉴权配置
 */
export enum PLUGIN_AUTH_TYPE {
    // 无
    NONE = 'none',
    // 预定义
    DINGTALK = 'dingtalk',
    WXWORK = 'wxwork',
    FEISHU = 'feishu',
    WECHAT_SERVICE = 'wechat_service',
    // 自定义
    BASIC_AUTH = 'basic_auth',
    OAUTH2_CLIENT_CREDENTIALS = 'oauth2_client_credentials',
    OAUTH2_AUTHORIZATION_CODE = 'oauth2_authorization_code'
}

/**
 * 插件身份验证
 */
export interface IPluginAuthTemplate {
    auth_type: PLUGIN_AUTH_TYPE;
    /* 认证入参 */
    conf_template: IPluginArgumentConf;
    /** refresh token鉴权获取token http请求结构 */
    refresh_token_request_template: IPluginArgumentConf;
    /** code换双token鉴权http请求结构 */
    oauth2_request_template: IPluginArgumentConf;
    app_auth_template?: IPluginArgumentConf;
    /* 认证返回参数 */
    credential_template: IPluginArgumentConf;
}

/**
 * 插件参数配置
 */
export interface IPluginArgumentConf {
    fields: PluginArgumentField[]; // 字段列表.
}

/**
 * 插件
 */
export type PluginId = string;

export interface IPlugin {
    _id: PluginId;
    version: number; // 版本号(用于插件版本控制)
    owner_id: string; // 插件制作者
    category?: string; // 分类
    tags?: string[]; // 标签
    fork_id?: PluginId; // fork 家族 Id
    upstream_id?: PluginId; // fork 直接上游 Id
    downstream_id?: PluginId; // 下游市场插件 Id.
    isv_id?: string; // 服务商 Id.
    fee?: PluginPayment;
    trigger_fees?: IPluginTriggerFee[];
    stage: PLUGIN_STAGE; // 插件生命周期
    status?: PLUGIN_PUBLISH_STATUS; // 插件发布状态
    is_enabled_by_default?: boolean; // 安装后是否默认启用
    is_official?: boolean; // 是否为官方插件
    is_recommended?: boolean; // 是否为推荐插件
    sponsor?: string; // 助力链接
    seq?: number; // 序号
    create_time: string; // 创建时间.
    update_time?: string; // 更新时间.
    semver: string; // 语义化版本号(用于界面展示)
    name: string;
    icon: string; // 图标
    overview: string; // 概述
    description: string; // 描述
    changelog?: string; // 更新说明
    screenshots?: IPluginScreenshot[]; // 截图等资源
    docs?: string; // 帮助文档链接
    demo?: string; // 模板链接
    authentication?: {
        type?: PLUGIN_AUTH_TYPE;
        /* 鉴权参数字段配置 */
        conf_template?: IPluginArgumentConf;
        app_auth_template?: IPluginArgumentConf;
        app_auth?: PluginArgumentValue[];
        oauth2_url?: string;
        /** code换token配置 */
        oauth2_request_conf?: PluginArgumentValue[];
        /** 账号信息json path */
        oauth2_account_json_path?: string;
        /** 刷新token */
        refresh_token_request_conf?: PluginArgumentValue[];

        access_token_json_path?: string;
        access_token_expires_in_json_path?: string;
        access_token_expires_in?: number;

        refresh_token_json_path?: string;
        refresh_token_expires_in_json_path?: string;
        refresh_token_expires_in?: number;
    };
    global_template?: IPluginArgumentConf; // 通用参数配置
    conf_docs?: string; // 插件配置模板文档
    i18n: Partial<
        Record<
            LOCALE_KEY,
            Pick<
                IPlugin,
                'name' | 'docs' | 'description' | 'overview' | 'screenshots' | 'icon' | 'changelog'
            >
        >
    >;
}

/**
 * 插件参数值：身份认证、通用参数、请求参数
 */
export interface IPluginArgumentValue<Type = PLUGIN_ARGUMENT_VALUE_FROM, Arg = unknown> {
    id: string;
    type?: PLUGIN_ARGUMENT_FIELD_TYPE;
    // 入参赋值规则
    resolver_type: Type;
    // 入参值
    resolver_arg?: Arg;
}

// vector 以外的 基础 resolver_type - resolver_arg 类型对应关系
interface IResolverArgBaseMap {
    /**
     * LITERAL:
     *  - text/date/select: string
     *  - number: number
     *  - user: {departs:string[],roles:string[],users: string[]}
     *  - dept: {departs:string[]}
     */
    [PLUGIN_ARGUMENT_VALUE_FROM.LITERAL]: unknown;
    [PLUGIN_ARGUMENT_VALUE_FROM.PROCESS_VAR]: string[];
    [PLUGIN_ARGUMENT_VALUE_FROM.EXPR]: string;
}
// Argument Conf Value 类型
type BaseArgumentValueMap = {
    [Key in keyof IResolverArgBaseMap]: IPluginArgumentValue<Key, IResolverArgBaseMap[Key]>;
};
export type BaseArgumentValue = BaseArgumentValueMap[keyof BaseArgumentValueMap];

/**
 * 子表单赋值方式
 */
export enum VectorIteratorType {
    /** 子表单整体赋值 */
    loop = 'loop',
    /** 逐行赋值 */
    map = 'map'
}

export interface VectorArgumentValue
    extends IPluginArgumentValue<PLUGIN_ARGUMENT_VALUE_FROM.VECTOR, BaseArgumentValue[][]> {
    /** 为空 = map */
    iterator_type?: VectorIteratorType;
    /** 子表单 widgetName */
    resolver_ctx?: string[];
}

export type PluginArgumentValue = BaseArgumentValue | VectorArgumentValue;

export type PluginAgentId = string;

/** 插件配置组视图（接口返回的完整列表，含默认组） */
export interface IPluginAgentConfigGroupView {
    config_group_id: string;
    name: string;
    is_default: boolean;
}

/** 插件来源 - 官方插件/企业插件 */
export type PluginStoreType = 'store' | 'enterprise';

/**
 * 已安装插件
 *
 * @description 当用户安装指定 Plugin 时, 则会在其名下生成一份 Plugin Agent.
 */
export interface IPluginAgent extends Omit<IPlugin, '_id'> {
    _id: PluginAgentId;
    plugin_id: PluginId;
    is_enabled: boolean; // 是否启用
    assignments?: PluginArgumentValue[]; // 通用参数值
    secret?: PluginArgumentValue[]; // 身份认证参数值
    is_private: boolean;
    store_type: PluginStoreType;
    subscription?: PluginSubscription;
    /** 是否使用了自定义环境 */
    has_custom_runtime?: boolean;
    /** 配置组完整视图；缺省时视为仅有默认组 */
    config_groups?: IPluginAgentConfigGroupView[];
}

/** 插件订阅状况 */
export interface PluginSubscription {
    expire_time: string; // 年付费插件过期时间
    remaining: number;
    type: PluginSubscriptionType;
}

// 插件函数类型
export enum PLUGIN_FUNCTION_TYPE {
    // 前端函数
    FRONT_END = 'applet',
    // 后端函数
    BACK_END = 'function'
}

export interface PluginRuntimeConfig {
    trigger_runtime_list: {
        value: PLUGIN_RUNTIME;
        /** 升级后的runtime */
        upgrade_runtime?: PLUGIN_RUNTIME;
        /** runtime是否部署 */
        enable: boolean;
        /** runtime是否弃用 */
        is_deprecated: boolean;
    }[];
    /** 市场插件的ip */
    store_plugin_ip_list: string[];
}

/**
 * 插件函数
 */
export interface IPluginFunction {
    _id: string;
    name: string;
    symbol: string; // 函数唯一标识
    seq?: number;
    arguments_template: IPluginArgumentConf; // 入参配置
    return_template: IPluginReturnConf; // 出参配置
    plugin_id: PluginId;
    create_time: string;
    update_time: string;
}
/**
 * 前端扩展
 */
export interface IPluginApplet extends IPluginFunction {
    source_code: string;
}
/**
 * 后端函数
 */
export interface IPluginTrigger extends IPluginFunction {
    /** 基础环境为 PLUGIN_RUNTIME，自定义环境为 runtime _id */
    runtime: PLUGIN_RUNTIME | string;
    runtime_list: PLUGIN_RUNTIME[];
    source_code: string;
}

// 插件返回参数类型
export enum PLUGIN_RETURN_TYPE {
    ANY = 'any',
    VECTOR = 'vector' // object[]
}

/**
 * 插件返回参数配置
 */
interface IPluginReturnField {
    id: string;
    label: string;
    parentLabel?: string; // 所属对象参数名称
    fieldType: PLUGIN_RETURN_TYPE;
    fieldConf: {};
}

export interface AnyReturnField extends IPluginReturnField {
    fieldType: PLUGIN_RETURN_TYPE.ANY;
    fieldConf: any;
}

export interface VectorReturnField extends IPluginReturnField {
    fieldType: PLUGIN_RETURN_TYPE.VECTOR;
    fieldConf: {
        fields?: AnyReturnField[]; // 回写到子表单
    };
}

export type PluginReturnField = AnyReturnField | VectorReturnField;

export interface IPluginReturnConf {
    fields: PluginReturnField[];
}

/**
 * 插件返回参数值
 */
export interface IPluginReturnValue {
    field: string; // 子字段格式：subformWidgetName.subFieldWidgetName
    value: string;
    type?: string; // widgetType
}

/**
 * 定义一个简陋的schema interface
 * @todo 如果后续项目使用了ajv，可以用ajv中的schema类型替换
 */
export interface BaseSchema {
    type?: 'object' | 'array' | 'number' | 'string' | 'boolean';
    properties?: Record<string, BaseSchema>;
    required?: string[];
    additionalProperties?: boolean;
    items?: BaseSchema;
    maxItems?: number;
    maxLength?: number;
    [key: string]: any;
}

// 插件函数不同类型入参，对应可选择的赋值规则
export interface IFunctionArgumentFrom {
    field_type: PLUGIN_ARGUMENT_FIELD_TYPE | PLUGIN_RETURN_TYPE;
    resolver_types: PLUGIN_ARGUMENT_VALUE_FROM[];
    field_schemas?: {
        conf: BaseSchema;
        conf_value: BaseSchema;
        definition: BaseSchema;
        runtime_value: BaseSchema;
    };
}

type ResolverArgMap = Partial<{
    [Key in keyof IResolverArgBaseMap]: {
        label?: string;
        arg: IResolverArgBaseMap[Key];
    };
}>;

// 插件函数入参可选择的表单字段
export interface IFunctionArgumentValueField {
    id: string;
    title: string;
    fields: PLUGIN_ARGUMENT_FIELD_TYPE[];
    from: string;
    resolvers: ResolverArgMap &
        Partial<{
            [PLUGIN_ARGUMENT_VALUE_FROM.VECTOR]: ResolverArgMap;
        }>;
}

export enum PLUGIN_RUNTIME {
    PYTHON36 = 'python36',
    PYTHON310 = 'python310',
    PYTHON314 = 'python314',
    NODEJS12 = 'nodejs12',
    NODEJS20 = 'nodejs20',
    NODEJS24 = 'nodejs24'
}
